import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiArrowLeft,
  FiArrowRight,
  FiBookOpen,
  FiBox,
  FiCompass,
  FiHome,
  FiLayers,
} from 'react-icons/fi';
import './NotFound.css';
import { useLocale } from '../context/LocaleContext';

const NotFound = () => {
  const { t } = useTranslation();
  const { localePath, formatNumber } = useLocale();
  const navigate = useNavigate();
  const quickLinks = [
    { to: '/services', icon: FiLayers, title: t('nav.services'), description: t('notFound.servicesHint') },
    { to: '/products', icon: FiBox, title: t('nav.products'), description: t('notFound.productsHint') },
    { to: '/articles', icon: FiBookOpen, title: t('nav.articles'), description: t('notFound.articlesHint') },
  ];

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate(localePath('/'));
  };

  return (
    <section className="not-found">
      <div className="not-found__glow not-found__glow--one" aria-hidden="true" />
      <div className="not-found__glow not-found__glow--two" aria-hidden="true" />

      <div className="not-found__shell">
        <div className="not-found__copy">
          <span className="not-found__badge">
            <i aria-hidden="true" />
            {t('notFound.badge')}
          </span>

          <p className="not-found__eyebrow">{t('notFound.eyebrow')}</p>
          <h1>{t('notFound.title')}</h1>
          <p className="not-found__description">
            {t('notFound.description')}
          </p>

          <div className="not-found__actions">
            <Link className="not-found__button not-found__button--primary" to={localePath('/')}>
              <FiHome aria-hidden="true" />
              {t('notFound.home')}
              <FiArrowLeft className="not-found__button-arrow" aria-hidden="true" />
            </Link>
            <button
              className="not-found__button not-found__button--secondary"
              type="button"
              onClick={handleGoBack}
            >
              <FiArrowRight aria-hidden="true" />
              {t('notFound.back')}
            </button>
          </div>
        </div>

        <div className="not-found__visual" aria-hidden="true">
          <div className="not-found__route-chip">
            <span />
            {t('notFound.route')}
          </div>

          <div className="not-found__orbit not-found__orbit--outer">
            <span className="not-found__orbit-dot" />
          </div>
          <div className="not-found__orbit not-found__orbit--inner" />

          <div className="not-found__number">
            <span>{formatNumber(4)}</span>
            <div className="not-found__zero">
              <FiCompass />
            </div>
            <span>{formatNumber(4)}</span>
          </div>

          <div className="not-found__path">
            <span />
            <span />
            <span />
          </div>
        </div>
      </div>

      <nav className="not-found__quick-links" aria-label={t('notFound.suggestedRoutes')}>
        <div className="not-found__quick-heading">
          <span>{t('notFound.suggestions')}</span>
          <i aria-hidden="true" />
        </div>

        <div className="not-found__quick-grid">
          {quickLinks.map(({ to, icon: Icon, title, description }) => (
            <Link className="not-found__quick-card" to={localePath(to)} key={to}>
              <span className="not-found__quick-icon">
                <Icon aria-hidden="true" />
              </span>
              <span className="not-found__quick-copy">
                <strong>{title}</strong>
                <small>{description}</small>
              </span>
              <FiArrowLeft className="not-found__quick-arrow" aria-hidden="true" />
            </Link>
          ))}
        </div>
      </nav>
    </section>
  );
};

export default NotFound;
